import {
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import * as Yup from 'yup';
import { IRole } from '../../../models/role.model';
import { AuthState } from '../../../state/auth';
import { AppState } from '../../../state/configureStore';
import { fetchAllRoleList, RoleState } from '../../../state/role';
import { User } from '../../../state/user';
import {
  clearWorkingGroup,
  createWorkingGroup,
  fetchWorkingGroup,
  updateWorkingGroup,
  WorkingGroup,
  WorkingGroupState,
} from '../../../state/workingGroup';
import {
  AutoComplete,
  FixedPositionButtons,
  Loading,
  PageContainer,
  PageTitle,
} from '../../components';
import UserComboBox from '../../components/UserComboBox';

interface WorkingGroupFormRouteParamsProps {
  id: string;
}

interface WorkingGroupFormProps
  extends RouteComponentProps<WorkingGroupFormRouteParamsProps> {}

const WorkingGroupSchema = Yup.object().shape({
  name: Yup.string()
    .strict(true)
    .trim()
    // .max(6, 'Name is exceeded character limit(6)')
    .required('Name is required'),
  description: Yup.string().nullable(true),
  ownerId: Yup.number().required('Owner is required'),
  roleId: Yup.number().required('Role is required'),
});

const WorkingGroupForm: React.FC<WorkingGroupFormProps> = (props) => {
  const dispatch = useDispatch();
  const { match, history } = props;
  const {
    params: { id: paramsId },
  } = match;
  const isUpdateMode = paramsId !== undefined;

  const { user, loading: authIsLoading } = useSelector<AppState, AuthState>(
    (state) => state.auth,
  );

  const { items: roles } = useSelector<AppState, RoleState>((state) => state.role);
  const roleList = roles.filter(
    (role) =>
      role.name === 'CS' ||
      role.name === 'NPD' ||
      role.name === 'SSO' ||
      role.name === 'ACCOUNT' ||
      role.name === 'INVOICE' ||
      role.name === 'ENTRY' ||
      role.name === 'QUOTATION' ||
      role.name === 'PLANNING',
  );

  const { item: workingGroup, loading: workingGroupIsLoading } = useSelector<
    AppState,
    WorkingGroupState
  >((state) => state.workingGroup);

  const isLoading = authIsLoading || workingGroupIsLoading;
  const defaultValues: WorkingGroup = {
    owner: user,
    ownerId: user ? user.id : undefined,
    name: '',
    description: '',
    roleId: 0,
    active: true,
  };

  const redirectToWorkingGroupList = () => {
    history.goBack();
  };

  const handleSubmit = (values: WorkingGroup, actions: FormikHelpers<WorkingGroup>) => {
    if (isUpdateMode) {
      dispatch(updateWorkingGroup({ values, history }));
    } else {
      dispatch(createWorkingGroup({ values, history }));
    }
    actions.setSubmitting(false);
  };

  useEffect(() => {
    dispatch(fetchAllRoleList());

    if (isUpdateMode) {
      dispatch(fetchWorkingGroup(+paramsId));
    } else {
      dispatch(clearWorkingGroup());
    }

    return function cleanUp() {
      dispatch(clearWorkingGroup());
    };
  }, [isUpdateMode, paramsId, dispatch]);

  return (
    <PageContainer disableOverflowX={true}>
      <Loading isLoading={isLoading}>
        <Formik
          enableReinitialize={true}
          initialValues={{ ...defaultValues, ...workingGroup }}
          validationSchema={WorkingGroupSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, errors, touched, submitForm, setFieldValue }) => {
            return (
              <Form>
                <PageTitle>
                  {!isUpdateMode ? `New Working Group` : `Update Working Group `}
                </PageTitle>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2">Name</Typography>
                      <TextField
                        name="name"
                        type="input"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={values.name || ''}
                        onChange={handleChange}
                        error={!!errors.name && !!touched.name}
                        helperText={errors.name}
                        InputProps={{
                          readOnly: isUpdateMode,
                        }}
                        placeholder="Working Group Name"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">Description</Typography>
                      <TextField
                        name="description"
                        type="input"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={values.description || ''}
                        onChange={handleChange}
                        error={!!errors.description && !!touched.description}
                        helperText={errors.description}
                        placeholder="Working Group Description"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">Status</Typography>
                      <Select
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        name="active"
                        value={values.active}
                        onChange={handleChange}
                      >
                        <MenuItem value={'true'}>Active</MenuItem>
                        <MenuItem value={'false'}>Inactive</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">Role</Typography>
                      <AutoComplete
                        name={'roleId'}
                        placeholder={'Select Group Role'}
                        options={roleList}
                        handleChange={(value: IRole) => {
                          setFieldValue('role', value ? value : undefined);
                          setFieldValue('roleId', value ? value.id : undefined);
                        }}
                        getOptionLabel={(option: IRole) => option.name!}
                        getOptionValue={(option: IRole) => '' + option.id!}
                        value={values.role}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">Owner</Typography>
                      <UserComboBox
                        handleChange={(value: User) => {
                          setFieldValue('owner', value ? value : undefined);
                          setFieldValue('ownerId', value ? value.id : undefined);
                        }}
                        selectedValue={values.owner as User}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
                <FixedPositionButtons
                  actions={[
                    {
                      name: !isUpdateMode ? `Create` : `Update`,
                      onClick: () => {
                        submitForm();
                      },
                      color: 'primary' as 'primary',
                      disabled: false,
                      icon: 'save',
                    },
                    {
                      name: 'CANCEL',
                      onClick: redirectToWorkingGroupList,
                      color: 'default' as 'default',
                      disabled: false,
                    },
                  ]}
                  open
                />
              </Form>
            );
          }}
        </Formik>
      </Loading>
    </PageContainer>
  );
};

export default WorkingGroupForm;
